import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import {
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from '@radix-ui/react-icons'
import { PageHeader } from '@/components/layout'
import { LoadingOrb } from '@/components/ui/loading-orb'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  useDefaultAssociateNameData,
  useDefaultAssociateNameMutations,
  useSelectedAccount,
} from './hooks'

const MAX_NAME_LENGTH = 80
const SYSTEM_DEFAULT_ASSOCIATE_NAME = 'Sarah'

const normalizeSearch = (value) => value.trim().toLowerCase()

const buildPortfolioSearchBlob = (row) =>
  [
    row.portfolioName,
    row.residentAdvocateFirstName,
    row.accountDefaultResidentAdvocateFirstName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

function ResultAlert({ result }) {
  if (!result) return null

  if (result.type === 'error') {
    return (
      <Alert variant="destructive">
        <AlertDescription>{result.message}</AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert>
      <CheckIcon className="h-4 w-4" />
      <AlertDescription>{result.message}</AlertDescription>
    </Alert>
  )
}

function MissingRowsAlert({ missingPortfolios }) {
  if (!missingPortfolios?.length) return null

  return (
    <Alert variant="destructive">
      <ExclamationTriangleIcon className="h-4 w-4" />
      <AlertDescription>
        {missingPortfolios.length} selected propert
        {missingPortfolios.length === 1 ? 'y' : 'ies'} could not be updated because the portfolio
        settings row is missing. Contact the engineering team for help.
      </AlertDescription>
    </Alert>
  )
}

function LoadingRows({ columns }) {
  return Array.from({ length: 6 }, (_, index) => (
    <TableRow key={index}>
      {Array.from({ length: columns }, (__, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton className="h-5 w-full" />
        </TableCell>
      ))}
    </TableRow>
  ))
}

function PortfolioOverrideSection({
  accounts,
  selectedAccountId,
  selectedAccount,
  onAccountChange,
  isAccountsLoading,
  portfolios,
  isLoading,
  mutation,
  onSubmit,
  result,
  missingPortfolios,
}) {
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [associateName, setAssociateName] = useState('')
  const deferredSearch = useDeferredValue(normalizeSearch(search))

  useEffect(() => {
    setSearch('')
    setSelectedIds([])
    setAssociateName('')
  }, [selectedAccountId])

  const filteredPortfolios = useMemo(() => {
    if (!deferredSearch) return portfolios
    return portfolios.filter((row) => buildPortfolioSearchBlob(row).includes(deferredSearch))
  }, [deferredSearch, portfolios])

  const selectedIdSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds])
  const selectedVisibleIds = filteredPortfolios.map((row) => Number(row.portfolioId))
  const allVisibleSelected =
    selectedVisibleIds.length > 0 && selectedVisibleIds.every((id) => selectedIdSet.has(id))

  const togglePortfolio = (portfolioId) => {
    const numericId = Number(portfolioId)
    setSelectedIds((current) =>
      current.includes(numericId)
        ? current.filter((id) => id !== numericId)
        : [...current, numericId],
    )
  }

  const toggleVisible = () => {
    if (allVisibleSelected) {
      const visibleSet = new Set(selectedVisibleIds)
      setSelectedIds((current) => current.filter((id) => !visibleSet.has(id)))
      return
    }

    setSelectedIds((current) => [...new Set([...current, ...selectedVisibleIds])])
  }

  const trimmedAssociateName = associateName.trim()
  const canSubmit =
    Boolean(selectedAccountId) &&
    selectedIds.length > 0 &&
    trimmedAssociateName.length > 0 &&
    !mutation.isPending

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    const response = await onSubmit({
      accountId: Number(selectedAccountId),
      portfolioIds: selectedIds,
      residentAdvocateFirstName: trimmedAssociateName,
    })
    if (response?.updatedRows?.length) {
      setAssociateName('')
      setSelectedIds([])
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle>Portfolio-level edit</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedAccount ? (
                <>
                  Set property-specific associate names for active properties in{' '}
                  <span data-pii="property">{selectedAccount.accountName}</span>.
                </>
              ) : (
                'Select an active account to load its properties.'
              )}
            </p>
          </div>
          <Badge variant="outline">{selectedIds.length} selected</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:max-w-md">
          <Label htmlFor="portfolio-level-account">Account</Label>
          <Select
            value={selectedAccountId}
            onValueChange={onAccountChange}
            disabled={isAccountsLoading || !accounts.length}
          >
            <SelectTrigger id="portfolio-level-account" data-pii="property">
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem
                  key={account.accountId}
                  value={String(account.accountId)}
                  data-pii="property"
                >
                  {account.accountName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedAccountId ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Select an account to load active properties.
          </div>
        ) : (
          <>
            <form className="grid gap-3 lg:grid-cols-[1fr_220px_auto]" onSubmit={handleSubmit}>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search property or name"
                  className="pl-9"
                />
              </div>
              <Input
                value={associateName}
                onChange={(event) => setAssociateName(event.target.value)}
                placeholder="Associate name"
                maxLength={MAX_NAME_LENGTH}
                data-pii="name"
              />
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving...' : 'Update properties'}
              </Button>
            </form>

            <ResultAlert result={result} />
            <MissingRowsAlert missingPortfolios={missingPortfolios} />

            <div className="overflow-hidden rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleVisible}
                        disabled={!filteredPortfolios.length}
                        aria-label="Select visible properties"
                      />
                    </TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Property associate name</TableHead>
                    <TableHead>Account default</TableHead>
                    <TableHead>Effective associate name</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <LoadingRows columns={6} />
                  ) : filteredPortfolios.length ? (
                    filteredPortfolios.map((row) => {
                      const portfolioId = Number(row.portfolioId)
                      const isSelected = selectedIdSet.has(portfolioId)
                      const hasSettingsRow = Boolean(row.settingsId)
                      const effectiveAssociateName =
                        row.residentAdvocateFirstName ||
                        row.accountDefaultResidentAdvocateFirstName ||
                        SYSTEM_DEFAULT_ASSOCIATE_NAME

                      return (
                        <TableRow key={portfolioId} className={isSelected ? 'bg-muted/40' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => togglePortfolio(portfolioId)}
                              aria-label={`Select ${row.portfolioName}`}
                            />
                          </TableCell>
                          <TableCell data-pii="property">{row.portfolioName}</TableCell>
                          <TableCell data-pii="name">
                            {row.residentAdvocateFirstName || (
                              <span className="text-muted-foreground">Not set</span>
                            )}
                          </TableCell>
                          <TableCell data-pii="name">
                            {row.accountDefaultResidentAdvocateFirstName || (
                              <span className="text-muted-foreground">Not set</span>
                            )}
                          </TableCell>
                          <TableCell data-pii="name">
                            {effectiveAssociateName || (
                              <span className="text-muted-foreground">Not set</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {hasSettingsRow ? (
                              <Badge variant="secondary">Ready</Badge>
                            ) : (
                              <Badge variant="destructive">Missing settings row</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-24 text-center text-sm text-muted-foreground"
                      >
                        No active properties found for this account.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function AccountDefaultSection({
  accounts,
  selectedAccountId,
  onAccountChange,
  isAccountsLoading,
  mutation,
  onSubmit,
}) {
  const selectedAccount = useSelectedAccount(accounts, selectedAccountId)
  const [associateName, setAssociateName] = useState('')
  const [result, setResult] = useState(null)
  const trimmedAssociateName = associateName.trim()
  const canSubmit = Boolean(selectedAccountId && trimmedAssociateName) && !mutation.isPending
  const effectiveDefault =
    selectedAccount?.residentAdvocateFirstName || SYSTEM_DEFAULT_ASSOCIATE_NAME

  useEffect(() => {
    setAssociateName('')
    setResult(null)
  }, [selectedAccountId])

  const handleAccountChange = (accountId) => {
    onAccountChange(accountId)
    setAssociateName('')
    setResult(null)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return

    try {
      const response = await onSubmit({
        accountId: selectedAccountId,
        residentAdvocateFirstName: trimmedAssociateName,
      })
      setResult({
        type: 'success',
        message: `Account default saved for ${response.account.accountName}.`,
      })
      setAssociateName('')
    } catch (error) {
      setResult({
        type: 'error',
        message: error.message || 'Failed to update account default.',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account-level edit</CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          Set the fallback associate name for the selected account.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:max-w-md">
          <Label htmlFor="account-level-account">Account</Label>
          <Select
            value={selectedAccountId}
            onValueChange={handleAccountChange}
            disabled={isAccountsLoading || !accounts.length}
          >
            <SelectTrigger id="account-level-account" data-pii="property">
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem
                  key={account.accountId}
                  value={String(account.accountId)}
                  data-pii="property"
                >
                  {account.accountName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedAccountId ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Select an account to edit its account default.
          </div>
        ) : (
          <>
            <form
              className="grid gap-3 lg:grid-cols-[minmax(240px,1fr)_auto]"
              onSubmit={handleSubmit}
            >
              <Input
                value={associateName}
                onChange={(event) => setAssociateName(event.target.value)}
                placeholder="Associate name"
                maxLength={MAX_NAME_LENGTH}
                data-pii="name"
              />
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving...' : 'Save default'}
              </Button>
            </form>

            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <Label className="text-xs uppercase text-muted-foreground">
                Stored account default
              </Label>
              <p className="mt-1 font-medium text-foreground" data-pii="name">
                {selectedAccount?.residentAdvocateFirstName || 'Not set'}
              </p>
              <Label className="mt-3 block text-xs uppercase text-muted-foreground">
                Effective default
              </Label>
              <p className="mt-1 font-medium text-foreground" data-pii="name">
                {effectiveDefault}
              </p>
            </div>

            <ResultAlert result={result} />
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function DefaultAssociateNamePage() {
  const {
    accounts,
    portfolios,
    selectedPortfolioAccountId,
    setSelectedPortfolioAccountId,
    isAccountsLoading,
    isPortfoliosLoading,
    isFetching,
    error,
    refetch,
  } = useDefaultAssociateNameData()
  const { updatePortfoliosMutation, updateAccountMutation } = useDefaultAssociateNameMutations()
  const selectedPortfolioAccount = useSelectedAccount(accounts, selectedPortfolioAccountId)
  const [selectedAccountDefaultAccountId, setSelectedAccountDefaultAccountId] = useState('')
  const [portfolioResult, setPortfolioResult] = useState(null)
  const [missingPortfolios, setMissingPortfolios] = useState([])

  const handlePortfolioAccountChange = (accountId) => {
    setSelectedPortfolioAccountId(accountId)
    setPortfolioResult(null)
    setMissingPortfolios([])
  }

  const handlePortfolioSubmit = async (payload) => {
    try {
      const response = await updatePortfoliosMutation.mutateAsync(payload)
      const updatedCount = response.updatedRows?.length || 0
      const missing = response.missingPortfolios || []
      setMissingPortfolios(missing)
      setPortfolioResult({
        type: 'success',
        message: `${updatedCount} propert${updatedCount === 1 ? 'y' : 'ies'} updated.`,
      })
      return response
    } catch (mutationError) {
      setMissingPortfolios([])
      setPortfolioResult({
        type: 'error',
        message: mutationError.message || 'Failed to update selected properties.',
      })
      throw mutationError
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Default Associate Name"
        description="Manage outbound associate names for portfolio overrides and account defaults."
      />

      <div className="flex-1 space-y-6 overflow-auto p-6">
        <div className="flex items-center justify-between rounded-md border border-border bg-card p-4">
          <div>
            <p className="text-sm font-medium text-foreground">Resident advocate naming</p>
            <p className="text-sm text-muted-foreground">
              Active accounts only. Property updates do not create missing settings rows.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={refetch} disabled={isFetching}>
            {isFetching ? (
              <LoadingOrb state="searching" size={16} aria-hidden="true" className="mr-2" />
            ) : (
              <ReloadIcon className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              {error.message || 'Failed to load default associate name data.'}
            </AlertDescription>
          </Alert>
        ) : null}

        <PortfolioOverrideSection
          accounts={accounts}
          selectedAccountId={selectedPortfolioAccountId}
          selectedAccount={selectedPortfolioAccount}
          onAccountChange={handlePortfolioAccountChange}
          isAccountsLoading={isAccountsLoading}
          portfolios={portfolios}
          isLoading={isPortfoliosLoading}
          mutation={updatePortfoliosMutation}
          onSubmit={handlePortfolioSubmit}
          result={portfolioResult}
          missingPortfolios={missingPortfolios}
        />

        <AccountDefaultSection
          accounts={accounts}
          selectedAccountId={selectedAccountDefaultAccountId}
          onAccountChange={setSelectedAccountDefaultAccountId}
          isAccountsLoading={isAccountsLoading}
          mutation={updateAccountMutation}
          onSubmit={updateAccountMutation.mutateAsync}
        />
      </div>
    </div>
  )
}
